import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useCart } from '../context/CartContext';
import { CONTACT_INFO } from '../data/mock';
import { CheckCircle, ArrowRight, Mail, Hash, DollarSign, Package } from 'lucide-react';

const PaymentSuccessPage = () => {
  const [orderData, setOrderData] = useState(null);
  const location = useLocation();
  const { clearCart } = useCart();

  useEffect(() => {
    document.title = 'Payment Successful | Axon Muscle';

    // Case 1: Auto-approved flow already saved lastOrder.
    const stored = sessionStorage.getItem('lastOrder');
    if (stored) {
      try { setOrderData(JSON.parse(stored)); } catch (e) { /* ignore */ }
      sessionStorage.removeItem('lastOrder');
      sessionStorage.removeItem('pendingOrder');
      return;
    }

    // Case 2: Returning from 3DS — promote pendingOrder to confirmed.
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    const oid = params.get('oid');
    const pending = sessionStorage.getItem('pendingOrder');
    if (pending) {
      try {
        const p = JSON.parse(pending);
        setOrderData({
          orderId: p.orderId || oid || '',
          orderReference: p.orderReference || ref || '',
          transactionId: p.transactionId || '',
          amount: p.amount,
          email: p.email,
          customerName: p.customerName,
        });
      } catch (e) { /* ignore */ }
      sessionStorage.removeItem('pendingOrder');
    } else if (ref || oid) {
      setOrderData({ orderReference: ref || '', orderId: oid || '' });
    }

    try { clearCart(); } catch (e) { /* ignore */ }
    try { window.history.replaceState({}, document.title, '/success'); } catch (e) { /* ignore */ }
  }, [location.search, clearCart]);

  const displayRef = orderData?.orderReference ||
    (orderData?.orderId ? orderData.orderId.slice(0, 8).toUpperCase() : '');

  return (
    <div className="min-h-screen bg-neutral-950">
      <Header />

      <main className="pt-[140px] pb-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div
            data-testid="payment-success-icon"
            className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-lg shadow-green-500/20"
          >
            <CheckCircle className="w-12 h-12 text-white" />
          </div>

          <h1
            data-testid="payment-success-title"
            className="font-display text-3xl md:text-4xl text-white uppercase tracking-tight mb-4"
          >
            Payment Successful!
          </h1>

          <p className="text-neutral-400 text-lg mb-8">
            Your order has been placed and payment processed successfully.
            {orderData?.email && (
              <> A confirmation has been sent to <span className="text-axon-500 font-medium">{orderData.email}</span>.</>
            )}
          </p>

          <div
            data-testid="payment-success-details"
            className="bg-neutral-900 rounded-2xl border border-neutral-800 p-6 mb-8 text-left space-y-4"
          >
            {displayRef && (
              <div className="flex items-center justify-between border-b border-neutral-800 pb-3">
                <div className="flex items-center gap-2 text-neutral-500 text-sm">
                  <Hash className="w-4 h-4" /> Order Reference
                </div>
                <span className="text-white font-mono text-sm" data-testid="payment-success-order-ref">
                  {displayRef}
                </span>
              </div>
            )}
            {orderData?.transactionId && (
              <div className="flex items-center justify-between border-b border-neutral-800 pb-3">
                <div className="flex items-center gap-2 text-neutral-500 text-sm">
                  <Hash className="w-4 h-4" /> Transaction ID
                </div>
                <span className="text-white font-mono text-sm">{orderData.transactionId}</span>
              </div>
            )}
            {typeof orderData?.amount === 'number' && (
              <div className="flex items-center justify-between border-b border-neutral-800 pb-3">
                <div className="flex items-center gap-2 text-neutral-500 text-sm">
                  <DollarSign className="w-4 h-4" /> Amount
                </div>
                <span className="text-white font-semibold">${orderData.amount.toFixed(2)}</span>
              </div>
            )}
            {orderData?.email && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-neutral-500 text-sm">
                  <Mail className="w-4 h-4" /> Email
                </div>
                <span className="text-white text-sm">{orderData.email}</span>
              </div>
            )}
          </div>

          <div className="bg-axon-600/10 border border-axon-500/30 rounded-2xl p-5 mb-8 flex items-start gap-3 text-left">
            <Package className="w-5 h-5 text-axon-500 flex-shrink-0 mt-0.5" />
            <p className="text-axon-400 text-sm leading-relaxed">
              We&apos;re preparing your order. You&apos;ll receive a tracking number by email
              once it ships.
            </p>
          </div>

          <Link to="/shop">
            <button
              data-testid="payment-success-continue-btn"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white font-bold uppercase tracking-wider rounded-xl transition-all shadow-button"
            >
              Continue Shopping
              <ArrowRight className="w-5 h-5" />
            </button>
          </Link>

          <p className="mt-8 text-xs text-neutral-500">
            Questions? Email <a href={`mailto:${CONTACT_INFO.email}`} className="text-axon-500 hover:underline">{CONTACT_INFO.email}</a>
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
